import React, { useEffect, useState, useMemo, useRef } from "react";
import { Pagination, Skeleton, Table } from "antd";
import { useSearchParams } from "react-router-dom";
import CustomExpandIcon from "./CustomExpandIcon";
import { useThunk } from "../../hooks/useThunk";
import CustomButton from "../Buttons/CustomButton";

const DataTable = ({
  columns,
  fetchData,
  rowKey = "_id",
  filters = {},
  initialPageSize = 10,
  tableStyle = {},
  headingBgColor = "#DDE1E6",
  headingTextColor = "#0F161E",
  enableUrlParams = true,
  customQueryParams,
  dataSource,
  hasSubTable = false, // Prop to enable sub-table
  subTableConfig = {}, // Configuration for sub-table (columns, data field)
  refreshTrigger = 0, // Refresh trigger for the table
  setStaticalData = null,
  setLoadingReport = null,
  highlightedRowId,
  setPageCallback,
  dataRef, // optional ref to expose current table data to parent
  useLoadMore = false, // Toggle between pagination and load more
  loadMoreText = "Load More",
  loadMoreButtonStyle = {},
  searchQuery,
  updateRowRef = null,
  removeRowRef = null,
  //  prop to determine if a row should be expandable
  expandableRowCheck = null,
  addRowRef = null,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(true);

  // Use the custom useThunk hook to fetch data
  const [doFetchData, isFetchingData, fetchDataError] = useThunk(fetchData);

  // Get pagination values from URL params or use defaults
  const [currentPage, setCurrentPage] = useState(
    Number(searchParams.get("page")) || 1,
  );

  const [prevPage, setPrevPage] = useState(null);

  const currentPageSize =
    Number(searchParams.get("pageSize")) || initialPageSize;

  // Update URL parameters and return the updated params object
  const updateUrlParams = (newParams) => {
    if (!enableUrlParams) return newParams;

    const updatedParams = new URLSearchParams(searchParams);

    // Update or remove parameters
    Object.entries(newParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        updatedParams.set(key, value.toString());
      } else {
        updatedParams.delete(key);
      }
    });

    setSearchParams(updatedParams, { replace: true });
    return newParams;
  };

  useEffect(() => {
    if (addRowRef) {
      addRowRef.current = (newRow) => {
        setData((prev) => {
          const next = [...prev, newRow];
          if (dataRef) {
            dataRef.current = [...next];
          }
          return next;
        });
      };
    }
  }, [addRowRef]);

  useEffect(() => {
    if (setPageCallback && !useLoadMore) {
      setPageCallback((page) => {
        handlePaginationChange(page, currentPageSize);
      });
    }
  }, [setPageCallback, useLoadMore, currentPage, currentPageSize]);

  useEffect(() => {
    // Create a reference to the previous filters
    const filterValues = Object.entries(filters).filter(
      ([_, value]) => value !== undefined && value !== null && value !== "",
    );

    if (filterValues.length > 0) {
      const newParams = new URLSearchParams(searchParams.toString());

      // Update filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== "") {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });

      // Reset page to 1 ONLY when filters change, not on component mount
      newParams.set("page", "1");

      // Only update if something actually changed to avoid unnecessary rerenders
      if (newParams.toString() !== searchParams.toString()) {
        setSearchParams(newParams);
      }
    }
  }, [filters]);

  // Get all current parameters (pagination, sorting, filters)
  const getCurrentParams = () => {
    const params = {
      page: currentPage,
      pageSize: currentPageSize,
      sortField: searchParams.get("sortField") || "",
      sortOrder: searchParams.get("sortOrder") || "",
    };

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params[key] = value;
      }
    });

    Array.from(searchParams.entries()).forEach(([key, value]) => {
      if (
        !params[key] &&
        !["page", "pageSize", "sortField", "sortOrder"].includes(key)
      ) {
        params[key] = value;
      }
    });

    return params;
  };

  // Load data from API
  const loadData = async (isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      // Get current parameters
      const params = getCurrentParams();

      // Convert page & pageSize to offset & limit for API
      const apiParams = { ...params };

      if (useLoadMore) {
        // For load more, calculate offset based on current data length
        apiParams.offset = isLoadMore ? data.length : 0;
        apiParams.limit = params.pageSize;
      } else {
        // For pagination, use normal page calculation
        apiParams.offset = (params.page - 1) * params.pageSize;
        apiParams.limit = params.pageSize;
      }

      delete apiParams.page;
      delete apiParams.pageSize;

      const backendParams = customQueryParams
        ? customQueryParams(apiParams)
        : apiParams;

      const result = await doFetchData(backendParams);

      if (result?.success) {
        const responseData = result.response?.data;
        const newItems = Array.isArray(responseData?.items)
          ? responseData.items
          : [];

        if (useLoadMore && isLoadMore) {
          // Append new data to existing data
          setData((prevData) => [...prevData, ...newItems]);
          if (dataRef) {
            dataRef.current = [...data, ...newItems];
          }
          // Check if there's more data to load
          setHasMoreData(newItems.length === params.pageSize);
        } else {
          // Replace data (for pagination or initial load)
          setData(newItems);
          if (dataRef) {
            dataRef.current = [...newItems];
          }
          if (useLoadMore) {
            setHasMoreData(newItems.length === params.pageSize);
          }
        }

        const total = responseData?.pagination?.totalDocuments || 0;
        setTotalItems(total);
      } else {
        console.error("Error fetching data:", result?.error);
        if (!isLoadMore) {
          setData([]);
          setTotalItems(0);
        }
        if (useLoadMore) {
          setHasMoreData(false);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (!isLoadMore) {
        setData([]);
        setTotalItems(0);
      }
      if (useLoadMore) {
        setHasMoreData(false);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Load data when component mounts or when relevant params change
  useEffect(() => {
    // Reset data when filters change or component mounts (but not for load more)
    if (useLoadMore) {
      setData([]);
      setHasMoreData(true);
    }
    loadData();
  }, [
    useLoadMore ? 1 : currentPage, // Only depend on page if not using load more
    currentPageSize,
    searchParams.get("sortField"),
    searchParams.get("sortOrder"),
    JSON.stringify(filters),
    refreshTrigger,
  ]);

  // Update Loading state for category report
  useEffect(() => {
    if (setLoadingReport) {
      setLoadingReport(isFetchingData);
    }
  }, [isFetchingData]);

  useEffect(() => {
    const items = document.querySelectorAll(
      ".custom-pagination .ant-pagination-item",
    );

    items.forEach((item) => {
      const page = Number(item.textContent.trim());

      item.classList.remove(
        "ant-pagination-item-active-prev",
        "ant-pagination-item-active-current",
      );

      if (page === currentPage) {
        item.classList.add("ant-pagination-item-active-current");
      } else if (page === prevPage) {
        item.classList.add("ant-pagination-item-active-prev");
      }
    });
  }, [currentPage, prevPage]);

  useEffect(() => {
    if (updateRowRef) {
      updateRowRef.current = (id, updatedFields) => {
        setData((prev) => {
          const next = prev.map((row) =>
            row._id === id ? { ...row, ...updatedFields } : row,
          );
          if (dataRef) {
            dataRef.current = [...next];
          }
          return next;
        });
      };
    }
  }, [updateRowRef]);

  useEffect(() => {
    if (removeRowRef) {
      removeRowRef.current = (id) => {
        setData((prev) => {
          const next = prev.filter((row) => row._id !== id);
          if (dataRef) {
            dataRef.current = [...next];
          }
          return next;
        });
      };
    }
  }, [removeRowRef]);

  // Handle pagination change
  const handlePaginationChange = (page, pageSize) => {
    if (useLoadMore) return;

    setPrevPage(currentPage); // store the previous
    setCurrentPage(page); // update current
    updateUrlParams({ page, pageSize });
  };

  // Handle load more button click
  const handleLoadMore = () => {
    if (!loadingMore && hasMoreData) {
      loadData(true);
    }
  };

  // Handle table sorting
  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter.order) {
      // Update sort params and reset to page 1
      updateUrlParams({
        sortField: sorter.field,
        sortOrder: sorter.order,
        page: 1,
      });
    } else if (searchParams.has("sortField") || searchParams.has("sortOrder")) {
      // Clear sorting if it was previously set
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("sortField");
      newParams.delete("sortOrder");
      setSearchParams(newParams);
    }
  };

  // Enhanced columns with conditional sorting
  const enhancedColumns = columns.map((col) => ({
    ...col,
    sorter: col.sortable === true ? true : false,
    sortOrder:
      searchParams.get("sortField") === col.key
        ? searchParams.get("sortOrder")
        : null,
    onCell: () => ({
      style: {
        maxWidth: col.maxWidth || "auto",
        whiteSpace: col.maxWidth ? "nowrap" : "pre-wrap",
        overflow: col.maxWidth ? "hidden" : "visible",
        textOverflow: col.maxWidth ? "ellipsis" : "clip",
        minWidth: col.minWidth || "100px", // default minWidth here
      },
    }),

    // Handle nested data paths in dataIndex
    render:
      col.render ||
      ((text, record) => {
        if (typeof col.dataIndex === "string") {
          return text;
        }
        // Handle array dataIndex for nested data
        if (Array.isArray(col.dataIndex)) {
          let value = record;
          for (const key of col.dataIndex) {
            value = value?.[key];
          }
          return value;
        }
        return text;
      }),
  }));

  // sub-table render function to handle both nested and parent data
  const expandedRowRender = (record) => {
    if (!hasSubTable || !subTableConfig.columns) {
      return null;
    }

    // Get the sub-table data from the specified data field
    const subTableData = record[subTableConfig.dataField] || [];

    // If no data, show empty message
    if (!Array.isArray(subTableData) || subTableData.length === 0) {
      return (
        <div className="text-center py-4 text-gray-500">No data found</div>
      );
    }

    return (
      <div className="w-full overflow-x-auto custom-table-container">
        <Table
          columns={subTableConfig.columns}
          dataSource={subTableData}
          pagination={false}
          rowKey={subTableConfig.rowKey || "_id"}
          style={{
            borderRadius: "8px",
            border: "1px solid #dde1e6",
            background: "#191919",
            minWidth: "100%",
            ...tableStyle,
          }}
          components={{
            header: {
              cell: (props) => (
                <th
                  {...props}
                  style={{
                    backgroundColor: headingBgColor,
                    fontFamily: "Inter",
                    fontWeight: 600,
                    fontSize: "14px",
                    color: headingTextColor,
                    letterSpacing: "0.014em",
                    padding: "12px",
                    borderBottom: "1px solid #505050",
                    lineHeight: "22px",
                    transition: "background 0.2s, border-color 0.2s",
                  }}
                />
              ),
            },

            body: {
              cell: (props) => (
                <td
                  {...props}
                  style={{
                    fontSize: "12px", // Change text size
                    fontFamily: "Public Sans",
                    color: "#E2E2E2", // Adjust text color if needed
                    padding: "12px",
                    lineHeight: "20px",
                    borderBottom: "1px solid #505050",
                    transition: "background 0.2s, border-color 0.2s", // Added transition
                  }}
                />
              ),
            },
          }}
        />
      </div>
    );
  };

  // Check if a row should be expandable
  const isRowExpandable = (record) => {
    if (expandableRowCheck) {
      return expandableRowCheck(record);
    }
    // Default check: if hasSubTable is true and dataField exists with data
    if (hasSubTable && subTableConfig.dataField) {
      const subData = record[subTableConfig.dataField];
      return Array.isArray(subData) && subData.length > 0;
    }

    return false;
  };

  // Create skeleton loader for the table
  const renderSkeleton = () => {
    const skeletonRows = Array(currentPageSize)
      .fill(null)
      .map((_, index) => ({
        key: `skeleton-${index}`,
        isSkeletonRow: true,
      }));

    // Custom render function for skeleton columns
    const skeletonColumns = enhancedColumns.map((col) => ({
      ...col,
      render: (text, record) => {
        if (record.isSkeletonRow) {
          return (
            <Skeleton.Button
              active
              style={{ width: "100%", height: 20 }}
              size="small"
            />
          );
        }
        return col.render ? col.render(text, record) : text;
      },
    }));

    return (
      <Table
        columns={skeletonColumns}
        dataSource={skeletonRows}
        pagination={false}
        rowKey="key"
        style={{
          borderRadius: "8px",
          border: "1px solid #dde1e6",
          background: "#191919",
          minWidth: "100%",
          ...tableStyle,
        }}
        components={{
          header: {
            cell: (props) => (
              <th
                {...props}
                style={{
                  backgroundColor: headingBgColor,
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontSize: "14px",
                  color: headingTextColor,
                  letterSpacing: "0.014em",
                  padding: "12px",
                  transition: "background 0.2s, border-color 0.2s",
                }}
              />
            ),
          },
          body: {
            cell: (props) => (
              <td
                {...props}
                style={{
                  fontSize: "12px",
                  fontFamily: "Inter",
                  color: "#333",
                  padding: "12px",
                  background: "#191919",
                  transition: "background 0.2s, border-color 0.2s",
                }}
              />
            ),
          },
        }}
      />
    );
  };

  return (
    <div>
      <div className="w-full overflow-x-auto custom-table-container">
        {loading ? (
          renderSkeleton()
        ) : (
          <Table
            columns={enhancedColumns}
            dataSource={data}
            rowKey={rowKey}
            loading={false} // We're handling loading state with Skeleton
            onChange={handleTableChange}
            pagination={false} // Disable built-in pagination
            rowClassName={(record, index) => {
              let className = index % 2 === 0 ? "even-row" : "odd-row";
              if (record._id === highlightedRowId)
                className += " highlighted-row";
              return className;
            }}
            locale={{
              emptyText: (
                <div className="text-inter font-semibold text-sm text-[#E2E2E2] flex items-center justify-center h-full p-4">
                  {searchQuery && searchQuery.trim() !== ""
                    ? `No search result found for: "${searchQuery}"`
                    : "No data"}
                </div>
              ),
            }}
            expandable={
              hasSubTable
                ? {
                    expandedRowRender,
                    expandRowByClick: false,
                    expandIcon: ({ expanded, onExpand, record }) => {
                      // Only show expand icon if row has expandable data
                      if (!isRowExpandable(record)) {
                        return <span style={{ marginRight: 4 }} />; // Empty space to maintain alignment
                      }

                      return (
                        <CustomExpandIcon
                          expanded={expanded}
                          onExpand={onExpand}
                          record={record}
                        />
                      );
                    },
                    // Only allow expansion for rows that have data
                    rowExpandable: (record) => isRowExpandable(record),
                  }
                : undefined
            }
            style={{
              borderRadius: "8px",
              border: "1px solid #dde1e6",
              background: "#191919",
              minWidth: "100%",
              marginTop: "24px",
              ...tableStyle,
            }}
            components={{
              header: {
                cell: (props) => (
                  <th
                    {...props}
                    style={{
                      backgroundColor: headingBgColor,
                      fontFamily: "Inter",
                      fontWeight: 600,
                      fontSize: "14px",
                      color: headingTextColor,
                      letterSpacing: "0.014em",
                      padding: "12px",
                      borderBottom: "1px solid #505050",
                      lineHeight: "22px",
                      transition: "background 0.2s, border-color 0.2s",
                    }}
                  />
                ),
              },
              body: {
                cell: (props) => (
                  <td
                    {...props}
                    style={{
                      fontSize: "12px", // Change text size
                      fontFamily: "Public Sans",
                      color: "#E2E2E2", // Adjust text color if needed
                      padding: "12px",
                      lineHeight: "20px",
                      borderBottom: "1px solid #505050",
                      transition: "background 0.2s, border-color 0.2s", // Added transition
                    }}
                  />
                ),
              },
            }}
          />
        )}
      </div>

      {/* Conditional rendering of Load More Button or Pagination */}
      {useLoadMore ? (
        // Load More Button
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "16px",
          }}
        >
          {hasMoreData && (
            <CustomButton
              onClick={handleLoadMore}
              disabled={loadingMore}
              style={{
                padding: "8px 24px",
                ...loadMoreButtonStyle,
              }}
            >
              {loadingMore ? "Loading..." : loadMoreText}
            </CustomButton>
          )}
        </div>
      ) : (
        // Pagination
        !dataRef && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "16px",
            }}
          >
            <Pagination
              current={currentPage}
              pageSize={currentPageSize}
              total={totalItems}
              onChange={handlePaginationChange}
              showSizeChanger={false}
              className="custom-pagination"
            />
          </div>
        )
      )}
    </div>
  );
};

export default DataTable;
